import React from "react";
import {
  Box,
  Paper,
  Typography,
  Icon,
  Switch,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useTheme,
  Zoom,
  Fade,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";

interface StaffBuilderSystemAccessProps {
  staffData: {
    is_pos_user: boolean;
    is_cloud_user: boolean;
    pos_pin?: string;
    is_pos_supervisor?: boolean;
    email: string;
    password?: string;
  };
  errors: {
    access?: string;
  };
  onSystemAccessToggle: (
    type: "pos" | "cloud"
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPOSSupervisorToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPinChange: (pin: string) => void;
  onPasswordChange: (password: string) => void;
}

export const StaffBuilderSystemAccess: React.FC<
  StaffBuilderSystemAccessProps
> = ({
  staffData,
  errors,
  onSystemAccessToggle,
  onPOSSupervisorToggle,
  onPinChange,
  onPasswordChange,
}) => {
  const theme = useTheme();
  const [pinGenerating, setPinGenerating] = React.useState(false);
  const [pinConfirmOpen, setPinConfirmOpen] = React.useState(false);
  const [generatedPin, setGeneratedPin] = React.useState("");
  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
  const [passwordMethod, setPasswordMethod] = React.useState<
    "manual" | "email" | "generate"
  >("manual");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState("");

  // Generate a random 5-digit PIN
  const generatePin = () => {
    setPinGenerating(true);
    const pin = Math.floor(10000 + Math.random() * 90000).toString();
    setGeneratedPin(pin);
    setPinConfirmOpen(true);
    setPinGenerating(false);
  };

  // Handle PIN confirmation
  const handlePinConfirm = () => {
    onPinChange(generatedPin);
    setPinConfirmOpen(false);
    toast.success("PIN code has been set successfully");
  };

  const validatePassword = (password: string): boolean => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasMinLength = password.length >= 8;

    if (!hasMinLength) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }
    if (!hasUpperCase || !hasLowerCase) {
      setPasswordError(
        "Password must contain both uppercase and lowercase letters"
      );
      return false;
    }
    if (!hasNumbers) {
      setPasswordError("Password must contain at least one number");
      return false;
    }
    if (!hasSpecialChar) {
      setPasswordError("Password must contain at least one special character");
      return false;
    }

    setPasswordError("");
    return true;
  };

  const generateSecurePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
    let password = "";

    // Ensure at least one of each required character type
    password += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
    password += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
    password += "0123456789"[Math.floor(Math.random() * 10)];
    password += "!@#$%^&*()"[Math.floor(Math.random() * 10)];

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    password = password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    setNewPassword(password);
    setConfirmPassword(password);
  };

  const handlePasswordSubmit = () => {
    if (passwordMethod === "manual" || passwordMethod === "generate") {
      if (newPassword !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      }

      if (!validatePassword(newPassword)) {
        return;
      }

      onPasswordChange(newPassword);
      setPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMethod("manual");
      toast.success("Password has been updated successfully");
    } else {
      toast.success("Password reset email sent to " + staffData.email);
      setPasswordDialogOpen(false);
    }
  };

  return (
    <Fade in timeout={450}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
          borderRadius: "12px",
          bgcolor: "background.paper",
          transition: "all 0.3s ease-in-out",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          "&:hover": {
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#0891B2",
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontFamily: "Montserrat",
              fontWeight: 600,
            }}
          >
            <Icon>admin_panel_settings</Icon>
            System Access
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontFamily: "Montserrat",
              mt: 0.5,
            }}
          >
            Configure access to POS and Cloud Backoffice systems
          </Typography>
          {errors.access && (
            <Typography
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontFamily: "Montserrat",
                color: "#DC2626",
              }}
            >
              <Icon fontSize="small" sx={{ color: "#DC2626" }}>
                error
              </Icon>
              {errors.access}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 3 }}>
          {/* POS Access */}
          <Box flex={1}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                height: "100%",
                transition: "all 0.2s ease-in-out",
                borderColor: staffData.is_pos_user ? "#0891B2" : "divider",
                "&:hover": {
                  borderColor: "#0891B2",
                  boxShadow: `0 0 0 1px #0891B2`,
                },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: staffData.is_pos_user
                    ? alpha("#0891B2", 0.04)
                    : "grey.50",
                  borderBottom: 1,
                  borderColor: "divider",
                  transition: "background-color 0.2s ease-in-out",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha("#0891B2", 0.1),
                    transition: "all 0.2s ease-in-out",
                    transform: staffData.is_pos_user
                      ? "scale(1.1)"
                      : "scale(1)",
                  }}
                >
                  <Icon
                    sx={{
                      color: staffData.is_pos_user
                        ? "#0891B2"
                        : "action.active",
                      fontSize: "24px",
                    }}
                  >
                    point_of_sale
                  </Icon>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontFamily: "Montserrat",
                      fontSize: "0.95rem",
                      color: staffData.is_pos_user ? "#0891B2" : "text.primary",
                    }}
                  >
                    POS Terminal Access
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "0.875rem",
                      lineHeight: 1.4,
                    }}
                  >
                    Allows the staff member to log in to Point of Sale terminals
                  </Typography>
                </Box>
                <Switch
                  checked={staffData.is_pos_user}
                  onChange={onSystemAccessToggle("pos")}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#0891B2",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#0891B2",
                    },
                  }}
                />
              </Box>

              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "Montserrat" }}
                  paragraph
                >
                  Allows the staff member to log in to Point of Sale terminals
                  and process sales. A 5-digit PIN will be used for quick
                  access.
                </Typography>

                <Divider sx={{ my: 2 }} />

                {staffData.is_pos_user ? (
                  <Zoom in timeout={300}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ color: "#0891B2" }} fontSize="small">
                          pin
                        </Icon>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontFamily: "Montserrat" }}
                        >
                          POS PIN Code
                        </Typography>
                      </Box>

                      {staffData.pos_pin ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                            p: 2,
                            bgcolor: alpha("#0891B2", 0.1),
                            borderRadius: "12px",
                            border: 1,
                            borderColor: "#0891B2",
                          }}
                        >
                          <Icon sx={{ color: "#0891B2" }}>check_circle</Icon>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontFamily: "Montserrat",
                              }}
                            >
                              POS PIN: {staffData.pos_pin}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: "Montserrat" }}
                            >
                              Staff member will use this 5-digit PIN to log in
                              to POS terminals
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={generatePin}
                            disabled={pinGenerating}
                            startIcon={<Icon fontSize="small">refresh</Icon>}
                            sx={{
                              fontFamily: "Montserrat",
                              borderRadius: "12px",
                            }}
                          >
                            Regenerate
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: "center", py: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={generatePin}
                            disabled={pinGenerating}
                            startIcon={<Icon>vpn_key</Icon>}
                            sx={{
                              borderRadius: "12px",
                              px: 3,
                              fontFamily: "Montserrat",
                              boxShadow: theme.shadows[2],
                              "&:hover": {
                                boxShadow: theme.shadows[4],
                              },
                            }}
                          >
                            {pinGenerating
                              ? "Generating..."
                              : "Generate 5-Digit PIN"}
                          </Button>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 1,
                              fontFamily: "Montserrat",
                            }}
                          >
                            Generate a random, secure PIN for POS login
                          </Typography>
                        </Box>
                      )}

                      {/* POS Supervisor Toggle */}
                      <Divider sx={{ my: 2.5 }} />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2.5,
                          bgcolor: (theme) =>
                            alpha(theme.palette.background.default, 0.6),
                          borderRadius: "12px",
                          border: "1px solid",
                          borderColor: staffData.is_pos_supervisor
                            ? "#0891B2"
                            : "divider",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "#0891B2",
                            bgcolor: alpha("#0891B2", 0.04),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: alpha("#0891B2", 0.1),
                              transition: "all 0.2s ease-in-out",
                              transform: staffData.is_pos_supervisor
                                ? "scale(1.1)"
                                : "scale(1)",
                            }}
                          >
                            <Icon
                              sx={{
                                color: staffData.is_pos_supervisor
                                  ? "#0891B2"
                                  : "action.active",
                                fontSize: "24px",
                              }}
                            >
                              supervisor_account
                            </Icon>
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                fontFamily: "Montserrat",
                                fontSize: "0.95rem",
                                color: staffData.is_pos_supervisor
                                  ? "#0891B2"
                                  : "text.primary",
                                mb: 0.5,
                              }}
                            >
                              POS Supervisor
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontFamily: "Montserrat",
                                fontSize: "0.875rem",
                                lineHeight: 1.4,
                              }}
                            >
                              Allow this user to manage other POS users and
                              handle supervisor-level operations
                            </Typography>
                          </Box>
                        </Box>
                        <Switch
                          checked={staffData.is_pos_supervisor}
                          onChange={onPOSSupervisorToggle}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#0891B2",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#0891B2",
                              },
                          }}
                        />
                      </Box>
                    </Box>
                  </Zoom>
                ) : (
                  <Fade in timeout={200}>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 3,
                        opacity: 0.6,
                      }}
                    >
                      <Icon
                        sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                      >
                        no_encryption
                      </Icon>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontFamily: "Montserrat" }}
                      >
                        POS access is disabled
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "Montserrat" }}
                      >
                        Toggle the switch above to enable
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Cloud Backoffice Access */}
          <Box flex={1}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                height: "100%",
                transition: "all 0.2s ease-in-out",
                borderColor: staffData.is_cloud_user ? "#0891B2" : "divider",
                "&:hover": {
                  borderColor: "#0891B2",
                  boxShadow: `0 0 0 1px #0891B2`,
                },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: staffData.is_cloud_user
                    ? alpha("#0891B2", 0.04)
                    : "grey.50",
                  borderBottom: 1,
                  borderColor: "divider",
                  transition: "background-color 0.2s ease-in-out",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha("#0891B2", 0.1),
                    transition: "all 0.2s ease-in-out",
                    transform: staffData.is_cloud_user
                      ? "scale(1.1)"
                      : "scale(1)",
                  }}
                >
                  <Icon
                    sx={{
                      color: staffData.is_cloud_user
                        ? "#0891B2"
                        : "action.active",
                      fontSize: "24px",
                    }}
                  >
                    cloud
                  </Icon>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontFamily: "Montserrat",
                      fontSize: "0.95rem",
                      color: staffData.is_cloud_user
                        ? "#0891B2"
                        : "text.primary",
                    }}
                  >
                    Cloud Backoffice Access
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "0.875rem",
                      lineHeight: 1.4,
                    }}
                  >
                    Allows the staff member to log in to the Cloud Backoffice
                    system
                  </Typography>
                </Box>
                <Switch
                  checked={staffData.is_cloud_user}
                  onChange={onSystemAccessToggle("cloud")}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#0891B2",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#0891B2",
                    },
                  }}
                />
              </Box>

              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "Montserrat" }}
                  paragraph
                >
                  Allows the staff member to log in to the Cloud Backoffice
                  system for business management. A secure password will be
                  required for login.
                </Typography>

                <Divider sx={{ my: 2 }} />

                {staffData.is_cloud_user ? (
                  <Zoom in timeout={300}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ color: "#0891B2" }} fontSize="small">
                          password
                        </Icon>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontFamily: "Montserrat" }}
                        >
                          Account Password
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                          p: 2,
                          bgcolor: alpha("#0891B2", 0.1),
                          borderRadius: "12px",
                          border: 1,
                          borderColor: "#0891B2",
                        }}
                      >
                        <Icon sx={{ color: "#0891B2" }}>lock</Icon>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              fontFamily: "Montserrat",
                            }}
                          >
                            {staffData.password
                              ? "Password Set"
                              : "No Password Set"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: "Montserrat" }}
                          >
                            {staffData.password
                              ? "Password has been configured for this account"
                              : "Please set a password for this account"}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          onClick={() => setPasswordDialogOpen(true)}
                          startIcon={<Icon fontSize="small">edit</Icon>}
                          sx={{
                            fontFamily: "Montserrat",
                            borderRadius: "12px",
                          }}
                        >
                          {staffData.password ? "Change" : "Set Password"}
                        </Button>
                      </Box>
                    </Box>
                  </Zoom>
                ) : (
                  <Fade in timeout={200}>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 3,
                        opacity: 0.6,
                      }}
                    >
                      <Icon
                        sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                      >
                        cloud_off
                      </Icon>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontFamily: "Montserrat" }}
                      >
                        Cloud access is disabled
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "Montserrat" }}
                      >
                        Toggle the switch above to enable
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* PIN Confirmation Dialog */}
        <Dialog
          open={pinConfirmOpen}
          onClose={() => setPinConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              "& .MuiDialogTitle-root": {
                p: 2.5,
                bgcolor: alpha("#0891B2", 0.04),
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiDialogContent-root": {
                p: 2.5,
              },
              "& .MuiDialogActions-root": {
                p: 2,
                bgcolor: "grey.50",
                borderTop: "1px solid",
                borderColor: "divider",
              },
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha("#0891B2", 0.1),
                }}
              >
                <Icon sx={{ color: "#0891B2" }}>pin</Icon>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  fontSize: "1.125rem",
                }}
              >
                Confirm PIN Code
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  bgcolor: alpha("#0891B2", 0.04),
                  border: "1px dashed",
                  borderColor: "#0891B2",
                  borderRadius: "12px",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    letterSpacing: 12,
                    fontWeight: "bold",
                    color: "#0891B2",
                    fontFamily: "Montserrat",
                    textShadow: "0 2px 4px rgba(0,0,0,0.08)",
                  }}
                >
                  {generatedPin}
                </Typography>
              </Paper>

              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  fontFamily: "Montserrat",
                  color: "text.primary",
                  fontWeight: 500,
                }}
              >
                This 5-digit PIN will be used by the staff member to log in to
                POS terminals.
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha("#0891B2", 0.04),
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon sx={{ color: "#0891B2" }}>info</Icon>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontFamily: "Montserrat",
                    textAlign: "left",
                  }}
                >
                  Make sure to share this PIN with the staff member securely.
                  They will need it to access the POS system.
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPinConfirmOpen(false)}
              sx={{
                fontFamily: "Montserrat",
                color: "text.secondary",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                "&:hover": {
                  bgcolor: "grey.100",
                  borderColor: "grey.300",
                },
                px: 3,
                borderRadius: "8px",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePinConfirm}
              variant="contained"
              startIcon={<Icon>save</Icon>}
              sx={{
                fontFamily: "Montserrat",
                px: 3,
                bgcolor: "#0891B2",
                borderRadius: "8px",
                boxShadow: "0 2px 8px " + alpha("#0891B2", 0.24),
                "&:hover": {
                  bgcolor: alpha("#0891B2", 0.9),
                },
              }}
            >
              Confirm & Save PIN
            </Button>
          </DialogActions>
        </Dialog>

        {/* Password Change Dialog */}
        <Dialog
          open={passwordDialogOpen}
          onClose={() => {
            setPasswordDialogOpen(false);
            setNewPassword("");
            setConfirmPassword("");
            setPasswordMethod("manual");
            setPasswordError("");
          }}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              "& .MuiDialogTitle-root": {
                p: 2.5,
                bgcolor: alpha("#0891B2", 0.04),
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiDialogContent-root": {
                p: 2.5,
              },
              "& .MuiDialogActions-root": {
                p: 2,
                bgcolor: "grey.50",
                borderTop: "1px solid",
                borderColor: "divider",
              },
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha("#0891B2", 0.1),
                }}
              >
                <Icon sx={{ color: "#0891B2" }}>password</Icon>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  fontSize: "1.125rem",
                }}
              >
                Change Password
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <RadioGroup
                value={passwordMethod}
                onChange={(e) =>
                  setPasswordMethod(
                    e.target.value as "manual" | "email" | "generate"
                  )
                }
                sx={{ mb: 3 }}
              >
                <FormControlLabel
                  value="manual"
                  control={
                    <Radio
                      sx={{
                        color: "#0891B2",
                        "&.Mui-checked": {
                          color: "#0891B2",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: "Montserrat" }}>
                      Set password manually
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="generate"
                  control={
                    <Radio
                      sx={{
                        color: "#0891B2",
                        "&.Mui-checked": {
                          color: "#0891B2",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: "Montserrat" }}>
                      Generate secure password
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="email"
                  control={
                    <Radio
                      sx={{
                        color: "#0891B2",
                        "&.Mui-checked": {
                          color: "#0891B2",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: "Montserrat" }}>
                      Send reset email to {staffData.email}
                    </Typography>
                  }
                />
              </RadioGroup>

              {(passwordMethod === "manual" ||
                passwordMethod === "generate") && (
                <Box sx={{ mb: 3 }}>
                  {passwordMethod === "generate" && (
                    <Button
                      variant="outlined"
                      onClick={generateSecurePassword}
                      startIcon={<Icon>autorenew</Icon>}
                      sx={{
                        mb: 2,
                        fontFamily: "Montserrat",
                        borderColor: "#0891B2",
                        color: "#0891B2",
                        "&:hover": {
                          borderColor: "#0891B2",
                          bgcolor: alpha("#0891B2", 0.04),
                        },
                      }}
                    >
                      Generate Password
                    </Button>
                  )}

                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        fontFamily: "Montserrat",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#0891B2",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#0891B2",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#0891B2",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            <Icon>
                              {showPassword ? "visibility_off" : "visibility"}
                            </Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!passwordError}
                    helperText={passwordError}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontFamily: "Montserrat",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#0891B2",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#0891B2",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#0891B2",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            <Icon>
                              {showPassword ? "visibility_off" : "visibility"}
                            </Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {passwordMethod === "email" && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    bgcolor: alpha("#0891B2", 0.04),
                    border: "1px solid",
                    borderColor: alpha("#0891B2", 0.2),
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: "Montserrat" }}
                  >
                    An email will be sent to {staffData.email} with instructions
                    to set a new password. The current password will continue to
                    work until they complete the reset process.
                  </Typography>
                </Paper>
              )}

              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha("#0891B2", 0.04),
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Icon sx={{ color: "#0891B2", mt: 0.5 }}>info</Icon>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: "Montserrat",
                      fontWeight: 600,
                      mb: 0.5,
                      color: "#0891B2",
                    }}
                  >
                    Password Requirements
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "Montserrat" }}>
                    • Minimum 8 characters
                    <br />
                    • At least one uppercase letter
                    <br />
                    • At least one lowercase letter
                    <br />
                    • At least one number
                    <br />• At least one special character
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPasswordDialogOpen(false);
                setNewPassword("");
                setConfirmPassword("");
                setPasswordMethod("manual");
                setPasswordError("");
              }}
              sx={{
                fontFamily: "Montserrat",
                color: "text.secondary",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                "&:hover": {
                  bgcolor: "grey.100",
                  borderColor: "grey.300",
                },
                px: 3,
                borderRadius: "8px",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasswordSubmit}
              variant="contained"
              startIcon={
                <Icon>{passwordMethod === "email" ? "send" : "save"}</Icon>
              }
              sx={{
                fontFamily: "Montserrat",
                px: 3,
                bgcolor: "#0891B2",
                borderRadius: "8px",
                boxShadow: "0 2px 8px " + alpha("#0891B2", 0.24),
                "&:hover": {
                  bgcolor: alpha("#0891B2", 0.9),
                },
              }}
            >
              {passwordMethod === "email"
                ? "Send Reset Email"
                : "Save Password"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Fade>
  );
};
